// Панель администратора: cine e în comunitate, ce rol are, cine e blocat.
//
// Regulile nu se pot ocoli din interfață, fiindcă le impune serverul: rolurile le
// schimbă doar proprietarul, proprietarul nu poate fi blocat, un admin nu poate bloca
// alt admin, și nimeni nu se poate bloca pe sine. Fără ele, doi administratori supărați
// pot lăsa comunitatea fără nimeni.

import { el, clear, bus } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { session, initials } from '../core/session.js';
import { guestGate } from '../ui/guest-gate.js';
import { modal, toast } from '../ui/components.js';

const ROL_ETICHETA = {
  owner: 'Владелец',
  admin: 'Админ',
  moderator: 'Модератор',
  member: 'Участник',
};

function cand(ts) {
  return new Date(ts * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: '2-digit' });
}

export class AdminSection {
  constructor(app) {
    this.app = app;
    this.items = [];
  }

  async mount(host) {
    this.root = el('div', { class: 'ideas-section' });
    host.append(this.root);
    this.offSession = bus.on('session:change', () => this.#load());
    await this.#load();
  }

  get #staff() {
    return ['owner', 'admin'].includes(session.user?.role);
  }

  async #load() {
    clear(this.root);
    if (!session.authenticated) {
      this.root.append(guestGate('Модерация сообщества. Войдите как владелец.'));
      return;
    }
    if (!this.#staff) {
      this.root.append(el('div', { class: 'ideas-empty' }, [
        el('div', { text: 'Раздел только для владельца и админов.' }),
      ]));
      return;
    }

    this.listEl = el('div', { class: 'admin-list' });
    this.linksEl = el('div', { class: 'admin-links' });
    this.plangeriEl = el('div', { class: 'admin-links' });

    this.root.append(
      el('div', { class: 'ideas-head' }, [
        el('h2', { class: 'ideas-title', text: 'Модерация' }),
        this.countEl = el('span', { class: 'reports-hint' }),
        el('button', { class: 'btn ideas-new', text: 'Обновить', onclick: () => this.#load() }),
      ]),
      el('div', { class: 'admin-body' }, [this.listEl, this.plangeriEl, this.linksEl]),
    );

    try {
      const data = await (await fetch('/api/admin/users')).json();
      this.items = data.items || [];
    } catch {
      this.items = [];
    }
    this.#render();
    this.#plangeri();
    this.#loadLinks();
  }

  /**
   * Plangerile nerezolvate.
   *
   * Stau sus, deasupra listei de linkuri: o platforma deschisa aduna plangeri, iar o
   * plangere pe care n-o vede nimeni e la fel de buna ca un buton care nu face nimic.
   */
  async #plangeri() {
    clear(this.plangeriEl);
    let data;
    try {
      data = await (await fetch('/api/admin/reports')).json();
    } catch {
      return;
    }

    this.plangeriEl.append(el('div', { class: 'set-card-head' }, [
      icon('warning', 15), el('h3', { text: 'Жалобы' }),
    ]));
    if (!data.items?.length) {
      this.plangeriEl.append(el('div', { class: 'ideas-empty-sub', text: 'Открытых жалоб нет.' }));
      return;
    }

    for (const r of data.items) {
      this.plangeriEl.append(el('div', { class: 'admin-plang' }, [
        el('div', { class: 'admin-plang-cap' }, [
          el('span', { class: 'admin-link-who', text: r.author }),
          el('span', { class: 'admin-badge', text: `жалоба от ${r.reporter}` }),
        ]),
        el('div', { class: 'admin-plang-text', text: r.post_text || '(без текста)' }),
        r.reason ? el('div', { class: 'admin-plang-motiv', text: r.reason }) : null,
        el('div', { class: 'set-actions' }, [
          el('button', {
            class: 'btn', text: 'Открыть автора',
            onclick: () => this.app.navigate(`/profile/${encodeURIComponent(r.author)}/`),
          }),
          el('button', {
            class: 'btn', text: 'Закрыть жалобу',
            onclick: async () => {
              await fetch(`/api/admin/reports/${r.id}/close`, { method: 'POST' });
              toast('Жалоба закрыта', 'success');
              this.#plangeri();
            },
          }),
        ]),
      ]));
    }
  }

  #render() {
    clear(this.listEl);
    this.countEl.textContent = `${this.items.length} аккаунтов`;

    for (const u of this.items) {
      const eu = u.id === session.user?.id;
      this.listEl.append(el('div', { class: `admin-row${u.banned ? ' banned' : ''}` }, [
        el('span', {
          class: 'chat-avatar sm', text: initials(u.username),
          style: { background: u.status.color },
        }),
        el('div', { class: 'admin-who' }, [
          el('button', {
            class: 'idea-author', text: u.username, style: { color: u.status.color },
            onclick: () => this.app.navigate(`/profile/${u.username}/`),
          }),
          el('span', { class: 'admin-mail' }, [
            el('span', { text: u.email || '—' }),
            u.email
              ? el('span', {
                class: `admin-badge ${u.email_verified ? 'ok' : 'warn'}`,
                text: u.email_verified ? 'подтверждена' : 'не подтверждена',
              })
              : null,
          ]),
        ]),
        el('span', { class: 'admin-meta', text: `с ${cand(u.created_at)}` }),
        el('span', { class: 'admin-meta', text: `рейтинг ${u.rating}` }),

        // Rolurile le schimbă doar proprietarul; serverul refuză oricum restul.
        session.user?.role === 'owner' && u.role !== 'owner' && !eu
          ? el('select', {
            class: 'admin-role',
            onchange: (e) => this.#setRole(u, e.target.value),
          }, ['admin', 'moderator', 'member'].map((r) => el('option', {
            value: r, text: ROL_ETICHETA[r], selected: r === u.role || undefined,
          })))
          : el('span', { class: `admin-rolelbl ${u.role}`, text: ROL_ETICHETA[u.role] || u.role }),

        u.role === 'owner' || eu
          ? el('span', { class: 'admin-meta', text: eu ? 'это вы' : '' })
          : el('button', {
            class: `btn ${u.banned ? '' : 'danger'} admin-ban`,
            text: u.banned ? 'Разблокировать' : 'Заблокировать',
            onclick: () => (u.banned ? this.#ban(u, false) : this.#askBan(u)),
          }),
      ]));
    }
  }

  async #setRole(u, role) {
    try {
      const resp = await fetch(`/api/admin/users/${u.id}/role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail);
      toast(`${u.username}: ${ROL_ETICHETA[role]}`, 'success');
    } catch (err) {
      toast(err.message || 'Не удалось изменить роль', 'error');
    }
    this.#load();
  }

  #askBan(u) {
    const motivEl = el('input', { class: 'auth-input', placeholder: 'Причина — увидит заблокированный' });
    modal({
      title: `Заблокировать ${u.username}?`,
      width: 440,
      body: el('div', { class: 'auth' }, [
        el('p', { class: 'auth-hint', text: 'Все открытые сессии закроются. Причина показывается при попытке войти.' }),
        el('label', { class: 'auth-field' }, [icon('warning', 13), motivEl]),
      ]),
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        {
          label: 'Заблокировать',
          primary: true,
          onClick: (c) => { c(); this.#ban(u, true, motivEl.value); },
        },
      ],
    });
  }

  async #ban(u, banned, reason = '') {
    try {
      const resp = await fetch(`/api/admin/users/${u.id}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ banned, reason }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail);
      toast(banned ? 'Аккаунт заблокирован' : 'Аккаунт разблокирован', 'info');
    } catch (err) {
      toast(err.message || 'Не получилось', 'error');
    }
    this.#load();
  }

  /**
   * Linkurile de confirmare și recuperare încă valabile.
   *
   * Există pentru cazul în care SMTP-ul nu e configurat: în loc ca oamenii să rămână
   * blocați, proprietarul le poate copia și trimite manual.
   */
  async #loadLinks() {
    clear(this.linksEl);
    let data;
    try {
      data = await (await fetch('/api/admin/links')).json();
    } catch {
      return;
    }

    this.linksEl.append(el('div', { class: 'set-card-head' }, [
      icon('key', 15), el('h3', { text: 'Ссылки подтверждения' }),
    ]));
    this.linksEl.append(el('p', {
      class: 'set-card-sub',
      text: data.mail_configured
        ? 'Почта настроена — письма уходят автоматически. Здесь только запасной вариант.'
        : 'Почта не настроена: письма не отправляются. Передайте ссылку участнику вручную.',
    }));

    if (!data.items?.length) {
      this.linksEl.append(el('div', { class: 'ideas-empty-sub', text: 'Активных ссылок нет.' }));
      return;
    }
    for (const l of data.items) {
      this.linksEl.append(el('div', { class: 'admin-link' }, [
        el('span', { class: 'admin-link-who', text: l.username }),
        el('span', { class: 'admin-badge', text: l.kind === 'confirm' ? 'подтверждение' : 'пароль' }),
        el('input', {
          class: 'auth-input mini', value: l.url, readonly: true,
          onclick: (e) => e.target.select(),
        }),
        el('button', {
          class: 'btn', text: 'Копировать',
          onclick: () => { navigator.clipboard?.writeText(l.url); toast('Ссылка скопирована', 'success'); },
        }),
      ]));
    }
  }

  unmount() {
    this.offSession?.();
    this.root?.remove();
  }
}
